import React, { useState } from "react";
import { HousePlus, FileText, AlertCircle, Shield, LucideIcon } from "lucide-react";
import { readableTextColour } from "../../utils/colourContrast";
import backgroundPattern from "../../images/vesta/vesta-property-pattern-01-teal-bg.svg";
import placeholderImage from "../../images/photo-coming-soon-300x300-1-square.jpg";
import {
  buildNewBuildReservationBookingUrl,
  NEW_BUILD_RESERVATION_FEES,
  NEW_BUILD_RESERVATION_SECTIONS,
} from "../../constants/newBuildReservation";

// Icons for the reservation term sections, ordered to match
// NEW_BUILD_RESERVATION_SECTIONS (period, fees, cancellation, identity).
const RESERVATION_SECTION_ICONS: LucideIcon[] = [
  HousePlus,
  FileText,
  AlertCircle,
  Shield,
];

const DEFAULT_THEME_COLOUR = "#004AAE";

interface NewBuildReserveModalProps {
  onClose: () => void;
  developmentName: string;
  siteName: string;
  thumbnailUrl?: string;
  themeColour1?: string;
  themeColour2?: string;
}

// Reserve modal for New Build developments. Unlike off-market reservations there is no
// Stripe payment: the buyer must accept the standard terms, then books a call via
// Calendly. Mounted only while open (parent gates with `&&`), so term acceptance resets
// each time it opens.
const NewBuildReserveModal: React.FC<NewBuildReserveModalProps> = ({
  onClose,
  developmentName,
  siteName,
  thumbnailUrl,
  themeColour1,
  themeColour2,
}) => {
  const [reserveTermsAccepted, setReserveTermsAccepted] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);

  const sectionHeaderColour = themeColour1 || DEFAULT_THEME_COLOUR;
  const sectionHeaderTextColour = readableTextColour(sectionHeaderColour);
  const bookButtonColour = themeColour2 || DEFAULT_THEME_COLOUR;
  const bookingUrl = buildNewBuildReservationBookingUrl(siteName, developmentName);

  const handleBookReservation = () => {
    if (!reserveTermsAccepted) return;
    const opened = window.open(bookingUrl, "_blank", "noopener,noreferrer");
    if (opened) {
      onClose();
      return;
    }
    // The browser blocked the popup — keep the modal open and surface a direct
    // link (an anchor click is not popup-blocked) so the booking path still works.
    setPopupBlocked(true);
  };

  return (
    <div
      className="fixed inset-0 z-[999999] overflow-y-auto flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.65)" }}
    >
      <div className="flex flex-col bg-white-f2f9fa border border-black shadow-sm rounded-xl overflow-hidden w-11/12 h-5/6 max-w-4xl">
        <div
          className="relative flex flex-col h-full"
          style={{
            backgroundImage: `url(${backgroundPattern})`,
            backgroundRepeat: "repeat",
            backgroundSize: "50px 50px",
          }}
        >
          {/* Header */}
          <div
            className="flex justify-between items-center py-3 px-4 border-b flex-shrink-0"
            style={{ backgroundColor: "white" }}
          >
            <h3 className="font-bold text-lg">Reserve this Development</h3>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Scrollable body */}
          <div className="p-4 overflow-y-auto flex-1 space-y-4">
            <p className="font-medium">
              You wish to reserve a property in this development
            </p>

            {/* Development summary: image + address, each in its own bordered box */}
            <div className="flex flex-wrap items-stretch justify-center gap-3">
              <div
                className="border rounded-md p-1 flex-shrink-0"
                style={{ backgroundColor: "white", borderColor: "black" }}
              >
                <img
                  className="rounded object-cover"
                  src={thumbnailUrl || placeholderImage}
                  alt="Development thumbnail"
                  style={{ width: "140px", height: "80px", objectFit: "cover" }}
                />
              </div>
              <div
                className="border rounded-md px-4 flex items-center justify-center"
                style={{ backgroundColor: "white", borderColor: "black" }}
              >
                <h3 className="text-lg text-center">{developmentName}</h3>
              </div>
            </div>

            {/* Key figures */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {NEW_BUILD_RESERVATION_FEES.map((fee) => (
                <div
                  key={fee.label}
                  className="bg-white border border-gray-300 rounded-md p-3 text-center"
                >
                  <p className="text-xs text-gray-500">{fee.label}</p>
                  <p className="text-lg font-bold text-black">{fee.value}</p>
                  <p className="text-[10px] text-gray-400 leading-tight mt-1">{fee.note}</p>
                </div>
              ))}
            </div>

            {/* Standard terms, grouped into styled sections (matches TermsConditions) */}
            {NEW_BUILD_RESERVATION_SECTIONS.map((section, sIdx) => {
              const SectionIcon =
                RESERVATION_SECTION_ICONS[sIdx % RESERVATION_SECTION_ICONS.length];
              return (
                <div
                  key={section.heading}
                  className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
                >
                  <div className="px-4 py-3" style={{ background: sectionHeaderColour }}>
                    <div className="flex items-center">
                      <SectionIcon
                        className="w-5 h-5 mr-2 flex-shrink-0"
                        style={{ color: sectionHeaderTextColour }}
                      />
                      <h4
                        className="text-lg font-semibold"
                        style={{ color: sectionHeaderTextColour }}
                      >
                        {sIdx + 1}. {section.heading}
                      </h4>
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    {section.items.map((item, iIdx) => (
                      <div key={iIdx} className="bg-gray-50 rounded-lg p-4">
                        <p className="text-gray-700 leading-relaxed text-center">
                          <span className="font-semibold">
                            {sIdx + 1}.{iIdx + 1}.
                          </span>{" "}
                          {item}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer: accept terms + book a call */}
          <div
            className="flex flex-col gap-2 py-3 px-4 border-t flex-shrink-0"
            style={{ backgroundColor: "white" }}
          >
            {popupBlocked && (
              <p className="text-sm text-red-600">
                Your browser blocked the booking window.{" "}
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium"
                  onClick={onClose}
                >
                  Click here to book your call
                </a>
                .
              </p>
            )}
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
              <label className="flex items-start sm:items-center text-sm cursor-pointer">
              <input
                type="checkbox"
                className="mr-2 mt-0.5 sm:mt-0 cursor-pointer"
                checked={reserveTermsAccepted}
                onChange={(e) => setReserveTermsAccepted(e.target.checked)}
              />
              I accept the Terms &amp; Conditions
            </label>
            <button
              type="button"
              disabled={!reserveTermsAccepted}
              onClick={handleBookReservation}
              className="py-3 px-4 inline-flex justify-center items-center gap-2 rounded-md font-medium shadow-sm transition-all text-sm whitespace-nowrap"
              style={{
                background: reserveTermsAccepted ? bookButtonColour : "grey",
                color: "#f2f9fa",
                cursor: reserveTermsAccepted ? "pointer" : "not-allowed",
                opacity: reserveTermsAccepted ? 1 : 0.6,
              }}
            >
              Book a Call to Reserve
            </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewBuildReserveModal;
